/**
 * Tenant Branding Utilities
 * 
 * Dynamically applies tenant-specific white-labeling (colors, logo, theme) to the application.
 * Integrates seamlessly with existing Tailwind CSS theme system via CSS custom properties.
 * Supports instant theme switching when tenant changes or global_admin switches tenants.
 * 
 * Core functionality:
 * - applyTenantColors: Sets CSS custom properties for tenant brand colors
 * - applyTenantThemeMode: Enforces tenant dark/light mode preferences
 * - getTenantLogo: Returns tenant logo URL for display
 * - resetToDefaultBranding: Restores default blue theme
 */

import { Tenant } from '@/types/tenant';
import { BLUE_THEME } from './theme-utils';

/**
 * Helper function to validate hex color format
 * Accepts formats: #RGB, #RRGGBB, #RRGGBBAA
 */
function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{3}){1,2}([A-Fa-f0-9]{2})?$/.test(color);
}

function normalizeHexColor(hexColor: string): string {
  const hex = hexColor.trim();
  if (!hex.startsWith("#")) return hex;
  if (hex.length === 4) {
    // #RGB -> #RRGGBB
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex;
}

function hexToRgb(hexColor: string): { r: number; g: number; b: number } {
  const hex = normalizeHexColor(hexColor).replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
}

function rgbToHslComponents(r: number, g: number, b: number): string {
  // Convert 0-255 RGB -> 0-1
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const hRound = Math.round(h);
  const sRound = Math.round(s * 100);
  const lRound = Math.round(l * 100);
  return `${hRound} ${sRound}% ${lRound}%`;
}

function hexToHslComponents(hexColor: string): string {
  const { r, g, b } = hexToRgb(hexColor);
  return rgbToHslComponents(r, g, b);
}

/**
 * Helper function to calculate contrasting text color (foreground) for a given background color
 * Uses relative luminance calculation to determine if white or black text provides better contrast
 * 
 * @param hexColor - Background color in hex format (e.g., '#3B82F6')
 * @returns Contrasting foreground color (white or black)
 */
function getContrastingTextColor(hexColor: string): string {
  const { r, g, b } = hexToRgb(hexColor);
  
  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return HSL components for dark/light text so `hsl(var(--primary-foreground))` remains valid.
  return luminance > 0.5 ? "0 0% 0%" : "0 0% 100%";
}

/**
 * Logs branding operations in development mode for debugging
 */
function logBrandingOperation(operation: string, details: any): void {
  if (import.meta.env.DEV) {
    console.log(`[TenantBranding] ${operation}:`, details);
  }
}

/**
 * Apply tenant-specific colors to CSS custom properties
 * 
 * Updates the application theme dynamically by setting CSS custom properties that Tailwind references.
 * If no tenant or no custom colors are provided, falls back to default BLUE_THEME colors.
 * 
 * CSS Variables Set:
 * - --primary: Primary brand color (e.g., button backgrounds, links)
 * - --primary-foreground: Contrasting text color for primary background
 * - --secondary: Secondary brand color (e.g., accents, highlights)
 * - --secondary-foreground: Contrasting text color for secondary background
 * 
 * @param tenant - Tenant object with branding configuration, or null for defaults
 */
export function applyTenantColors(tenant: Tenant | null): void {
  const root = document.documentElement;
  
  try {
    // Case 1: No tenant provided - use default blue theme
    if (!tenant) {
      logBrandingOperation('No tenant provided', { action: 'applying default colors' });

      // Prefer the HSL defaults declared in src/index.css (keeps `hsl(var(--primary))` valid).
      root.style.removeProperty("--primary");
      root.style.removeProperty("--primary-foreground");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--secondary-foreground");
      return;
    }
    
    // Case 2: Tenant exists but no custom colors - use default blue theme
    if (!tenant.primary_color && !tenant.secondary_color) {
      logBrandingOperation('Tenant has no custom colors', { 
        tenantId: tenant.id, 
        tenantName: tenant.name,
        action: 'applying default colors'
      });

      // Prefer the HSL defaults declared in src/index.css (keeps `hsl(var(--primary))` valid).
      root.style.removeProperty("--primary");
      root.style.removeProperty("--primary-foreground");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--secondary-foreground");
      return;
    }
    
    // Case 3: Apply tenant's custom colors
    logBrandingOperation('Applying tenant colors', {
      tenantId: tenant.id,
      tenantName: tenant.name,
      primaryColor: tenant.primary_color,
      secondaryColor: tenant.secondary_color
    });
    
    // Apply primary color with validation
    if (tenant.primary_color) {
      if (isValidHexColor(tenant.primary_color)) {
        root.style.setProperty("--primary", hexToHslComponents(tenant.primary_color));
        root.style.setProperty("--primary-foreground", getContrastingTextColor(tenant.primary_color));
      } else {
        console.warn(`[TenantBranding] Invalid primary color format: ${tenant.primary_color}, using default`);
        root.style.setProperty("--primary", hexToHslComponents(BLUE_THEME.colors.primary));
        root.style.setProperty("--primary-foreground", getContrastingTextColor(BLUE_THEME.colors.primary));
      }
    } else {
      // No primary color - use default
      root.style.setProperty("--primary", hexToHslComponents(BLUE_THEME.colors.primary));
      root.style.setProperty("--primary-foreground", getContrastingTextColor(BLUE_THEME.colors.primary));
    }
    
    // Apply secondary color with validation
    if (tenant.secondary_color) {
      if (isValidHexColor(tenant.secondary_color)) {
        root.style.setProperty("--secondary", hexToHslComponents(tenant.secondary_color));
        root.style.setProperty("--secondary-foreground", getContrastingTextColor(tenant.secondary_color));
      } else {
        console.warn(`[TenantBranding] Invalid secondary color format: ${tenant.secondary_color}, using default`);
        root.style.setProperty("--secondary", hexToHslComponents(BLUE_THEME.colors.primaryLight));
        root.style.setProperty("--secondary-foreground", getContrastingTextColor(BLUE_THEME.colors.primaryLight));
      }
    } else {
      // No secondary color - use default (lighter variant of primary if available, else default)
      if (tenant.primary_color && isValidHexColor(tenant.primary_color)) {
        // Use a slightly lighter version of primary for secondary
        root.style.setProperty("--secondary", hexToHslComponents(tenant.primary_color));
        root.style.setProperty("--secondary-foreground", getContrastingTextColor(tenant.primary_color));
      } else {
        root.style.setProperty("--secondary", hexToHslComponents(BLUE_THEME.colors.primaryLight));
        root.style.setProperty("--secondary-foreground", getContrastingTextColor(BLUE_THEME.colors.primaryLight));
      }
    }
  } catch (error) {
    console.error('[TenantBranding] Error applying tenant colors:', error);
    
    // Fallback to default theme on error
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primary-foreground");
    root.style.removeProperty("--secondary");
    root.style.removeProperty("--secondary-foreground");
  }
}

/**
 * Apply tenant theme mode preferences (dark/light mode enforcement)
 * 
 * Integrates with next-themes package to respect tenant's theme preferences.
 * If tenant only enables one mode, that mode is enforced by adding/removing 'dark' class on root element.
 * If both modes are enabled, user preference is respected (no forced mode).
 * 
 * Theme Mode Logic:
 * - dark_mode_enabled=true, light_mode_enabled=false: Force dark mode
 * - dark_mode_enabled=false, light_mode_enabled=true: Force light mode
 * - dark_mode_enabled=true, light_mode_enabled=true: Allow user choice (default)
 * - dark_mode_enabled=false, light_mode_enabled=false: Allow user choice (edge case)
 * 
 * @param tenant - Tenant object with theme preferences, or null for no enforcement
 */
export function applyTenantThemeMode(tenant: Tenant | null): void {
  const root = document.documentElement;
  
  try {
    // No tenant - allow user preference (no forced mode)
    if (!tenant) {
      logBrandingOperation('No tenant provided', { action: 'no theme mode enforcement' });
      return;
    }
    
    const { dark_mode_enabled, light_mode_enabled } = tenant;
    
    logBrandingOperation('Checking tenant theme mode preferences', {
      tenantId: tenant.id,
      tenantName: tenant.name,
      darkModeEnabled: dark_mode_enabled,
      lightModeEnabled: light_mode_enabled
    });
    
    // Case 1: Only dark mode enabled - force dark mode
    if (dark_mode_enabled && !light_mode_enabled) {
      logBrandingOperation('Forcing dark mode', { tenantId: tenant.id });
      root.classList.add('dark');
      return;
    }
    
    // Case 2: Only light mode enabled - force light mode
    if (!dark_mode_enabled && light_mode_enabled) {
      logBrandingOperation('Forcing light mode', { tenantId: tenant.id });
      root.classList.remove('dark');
      return;
    }
    
    // Case 3: Both modes enabled or both disabled - allow user preference
    // No forced mode, next-themes will handle user preference
    logBrandingOperation('Allowing user theme preference', { 
      tenantId: tenant.id,
      bothEnabled: dark_mode_enabled && light_mode_enabled,
      bothDisabled: !dark_mode_enabled && !light_mode_enabled
    });
  } catch (error) {
    console.error('[TenantBranding] Error applying tenant theme mode:', error);
    // On error, don't force any mode - let user preference prevail
  }
}

/**
 * Get tenant logo URL for display in the application
 * 
 * Returns the tenant's custom logo URL if configured, otherwise returns null.
 * Calling code should handle null case by displaying default logo.
 * 
 * @param tenant - Tenant object with logo configuration, or null
 * @returns Logo URL string if configured, null otherwise
 */
export function getTenantLogo(tenant: Tenant | null): string | null {
  if (!tenant || !tenant.logo_url) {
    logBrandingOperation('No tenant logo', { 
      hasTenant: !!tenant,
      action: 'returning null for default logo'
    });
    return null;
  }
  
  logBrandingOperation('Tenant logo found', {
    tenantId: tenant.id,
    tenantName: tenant.name,
    logoUrl: tenant.logo_url
  });
  
  return tenant.logo_url;
}

/**
 * Reset branding to default blue theme
 * 
 * Restores default BLUE_THEME colors by removing all tenant-specific customizations.
 * Used when logging out, switching tenants, or when tenant data is unavailable.
 * Ensures application always has valid theme colors applied.
 */
export function resetToDefaultBranding(): void {
  const root = document.documentElement;
  
  try {
    logBrandingOperation('Resetting to default branding', { theme: 'BLUE_THEME' });
    
    // Apply default blue theme colors
    root.style.setProperty('--primary', BLUE_THEME.colors.primary);
    root.style.setProperty('--primary-foreground', BLUE_THEME.colors.textPrimary);
    root.style.setProperty('--secondary', BLUE_THEME.colors.primaryLight);
    root.style.setProperty('--secondary-foreground', BLUE_THEME.colors.textPrimary);
    
    // Remove any forced theme mode (allow user preference)
    // Note: We don't forcibly remove 'dark' class here as that should be managed by next-themes
    // This only resets colors, not theme mode preference
    
    logBrandingOperation('Default branding applied successfully', {
      primary: BLUE_THEME.colors.primary,
      secondary: BLUE_THEME.colors.primaryLight
    });
  } catch (error) {
    console.error('[TenantBranding] Error resetting to default branding:', error);
  }
}
